import React, { useEffect, useState, useCallback } from 'react';
import { Loader2 } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import UploadHistoryFilter from './UploadHistoryFilter.tsx';
import UploadHistoryTable from './UploadHistoryTable.tsx';
import PaginationControl from './PaginationControl.tsx';
import { getUserTunesHistory } from '../../services/tuneService.ts';
import { toastHelper } from '../../utils/toastHelper';
import './UploadHistory.css';

interface Upload {
  id: number;
  video_title: string;
  upload_date: string | null;
  scheduled: boolean;
  youtube_id: string | null;
  youtube_edit: string;
  youtube_watch: string;
}

function UploadHistory() {
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);

  const [beforeDate, setBeforeDate] = useState<Dayjs | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('archived');
  const [archivedSubFilter, setArchivedSubFilter] = useState<string | null>('archived');
  
  // Sorting state - persists across page changes
  const [orderBy, setOrderBy] = useState<'video_title' | 'upload_date'>('video_title');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');

  const fetchSchedules = async (page: number) => {
    try {
      setLoading(true);
      setError(null);

      // Determine the actual status filter to send to API
      let actualStatusFilter: string | undefined;
      if (statusFilter === 'upcoming') {
        actualStatusFilter = 'upcoming';
      } else if (statusFilter === 'archived') {
        // Use the archived sub-filter when archived is selected
        // 'archived' means show both scheduled and instant
        // 'archived-scheduled' or 'archived-instant' means show only that type
        actualStatusFilter = archivedSubFilter || 'archived';
      }

      const filters = {
        upload_date_before: beforeDate ? beforeDate.endOf('day').toISOString() : undefined,
        status: actualStatusFilter,
        sort_by: orderBy,
        sort_order: order,
      };

      const response = await getUserTunesHistory(page, 10, filters);
      const uploadsData = response.data?.data || [];
      const totalPagesValue = response.data?.total_pages || 1;
      
      setUploads(uploadsData);
      setTotalPages(totalPagesValue);
    } catch (err: any) {
      setError('Failed to load uploads.');
      toastHelper.newMessage('error', err.title || 'Error', err.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchedules(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, beforeDate, statusFilter, archivedSubFilter, orderBy, order]);

  // Stable handlers using useCallback - never change, so filter never re-renders
  const handleBeforeDateChange = useCallback((newValue: Dayjs | null) => {
    setBeforeDate(newValue);
    setCurrentPage(1);
  }, []);

  const handleStatusFilterChange = useCallback((value: string) => {
    setStatusFilter(value);
    setCurrentPage(1);
  }, []);

  const handleArchivedSubFilterChange = useCallback((value: string) => {
    setArchivedSubFilter(value);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((_: unknown, value: number) => {
    setCurrentPage(value);
  }, []);

  const handleSort = useCallback((property: 'video_title' | 'upload_date') => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setCurrentPage(1); // Reset to first page when sorting changes
  }, [orderBy, order]);

  const handleSortChange = useCallback((newOrderBy: 'video_title' | 'upload_date', newOrder: 'asc' | 'desc') => {
    setOrderBy(newOrderBy);
    setOrder(newOrder);
    setCurrentPage(1); // Reset to first page when sorting changes
  }, []);

  return (
    <div className="upload-management-container">
      <UploadHistoryFilter
        beforeDate={beforeDate}
        onBeforeDateChange={handleBeforeDateChange}
        onStatusFilterChange={handleStatusFilterChange}
        onArchivedSubFilterChange={handleArchivedSubFilterChange}
      />

      {loading ? (
        <div className="upload-management-loading flex items-center justify-center min-h-[280px]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : error ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-destructive">{error}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          <UploadHistoryTable 
            uploads={uploads} 
            statusFilter={statusFilter}
            orderBy={orderBy}
            order={order}
            onSort={handleSort}
            onSortChange={handleSortChange}
          />
          <PaginationControl
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
}

export default UploadHistory;
